import { Box, useMediaQuery, useTheme } from '@mui/material';
import CheckboxSelectItem from 'components/checkbox-select-item';
import DatePicker from 'components/date-picker';
import FilterSidebar from 'components/filter-sidebar';
import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import Switch from 'components/switch';
import TextField from 'components/text-field';
import { Search } from 'lucide-react';
import React from 'react';
import { useIntl } from 'react-intl';
import { usePondsState } from 'views/production-systems/context/ponds-context';
import { ListPondsFilters } from 'views/production-systems/types/list-ponds-filters.type';
import { Pond, PondType, RegistrationDateType } from 'views/production-systems/types/pond.type';
import { Zone } from 'views/production-systems/types/zone.type';

type ParamTypes = {
  globalSiteId: string;
};

interface Props {
  filters: ListPondsFilters;
  clearFilters: () => void;
  updateFilters: (filters: ListPondsFilters) => void;
  zones: Zone[] | undefined;
  renderPonds: Pond[];
}

const PAGE_NAME_ID = 'productionSystems.farmOverview';

const ListPondsFilter: React.FC<Props> = ({ filters, clearFilters, updateFilters, zones, renderPonds }) => {
  const intl = useIntl();
  const { registrationDate, changeRegistrationDate, selectedPonds, setSelectedPonds } = usePondsState();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <FilterSidebar
      showClearFilter={
        !!filters.zoneId ||
        !!filters.searchValue ||
        !filters.showActivePonds ||
        !filters.showInactivePonds ||
        filters.selectedPondTypes.length < Object.values(PondType).length ||
        filters.enableKg !== true
      }
      clearFilters={clearFilters}>
      <Box width={'100%'} display={'flex'} gap={4} flexDirection={isMobile ? 'column' : 'row'} justifyContent={'flex-end'}>
        <Select
          label={intl.formatMessage({ id: `${PAGE_NAME_ID}.filters.zone` })}
          value={filters.zoneId}
          onChange={(event) => {
            let newFilters = { ...filters, zoneId: String(event.target.value) };
            updateFilters(newFilters);
            setSelectedPonds([]);
          }}>
          {zones?.map((zone) => (
            <RadioSelectItem key={zone.id} value={zone.id}>
              {zone.name}
            </RadioSelectItem>
          ))}
        </Select>
        <Select
          multiple
          label={intl.formatMessage({ id: `${PAGE_NAME_ID}.filters.pondType` })}
          value={filters.selectedPondTypes}
          checkAll={{
            value: filters.selectedPondTypes.length === Object.keys(PondType).length,
            onChange: () => {
              let newFilters = {
                ...filters,
                selectedPondTypes: filters.selectedPondTypes.length === Object.keys(PondType).length ? [] : Object.values(PondType),
              };

              updateFilters(newFilters);
            },
          }}
          onChange={(event) => {
            let newFilters = { ...filters, selectedPondTypes: event.target.value as string[] };
            updateFilters(newFilters);
          }}>
          {Object.entries(PondType)
            .filter((type) => !isNaN(Number(type[0])) === false)
            .sort((a, b) =>
              a[0].localeCompare(b[0], undefined, {
                numeric: true,
              })
            )
            ?.map((type) => (
              <CheckboxSelectItem key={type[0]} value={type[1]} checked={filters.selectedPondTypes.includes(type[1])}>
                {intl.formatMessage({ id: `${PAGE_NAME_ID}.filters.pondTypes.${type[0]}` })}
              </CheckboxSelectItem>
            ))}
        </Select>
        <DatePicker
          fullWidth={isMobile}
          disableFuture
          label={intl.formatMessage({ id: `${PAGE_NAME_ID}.filters.registrationDate` })}
          value={registrationDate}
          format={'DD/MM/YYYY'}
          onChange={(date) => {
            changeRegistrationDate(date, RegistrationDateType.overview);
          }}
        />
        <TextField
          value={filters.searchValue}
          label={intl.formatMessage({ id: 'general.search' })}
          onChange={(event) => {
            let newFilters = { ...filters, searchValue: event.target.value as string };

            updateFilters(newFilters);
          }}
          endAdornment={<Search size={18} />}
        />
      </Box>
      <Box display={'flex'} flexDirection={'row'} gap={4} justifyContent={'flex-end'} alignItems={'center'}>
        <Switch
          sx={{ textAlign: 'right', width: { xs: '100%', md: 'auto' } }}
          label={intl.formatMessage({ id: `${PAGE_NAME_ID}.filters.poundsOrKg` })}
          value={filters.enableKg}
          onChange={() => {
            let newFilters = { ...filters, enableKg: !filters.enableKg };
            updateFilters(newFilters);
          }}
        />
        <Switch
          sx={{ textAlign: 'right', width: { xs: '100%', md: 'auto' } }}
          label={intl.formatMessage({ id: `${PAGE_NAME_ID}.filters.showActive` })}
          value={filters.showActivePonds}
          onChange={() => {
            let newFilters = { ...filters, showActivePonds: !filters.showActivePonds };
            updateFilters(newFilters);
          }}
        />
        <Switch
          sx={{ textAlign: 'right', width: { xs: '100%', md: 'auto' } }}
          label={intl.formatMessage({ id: `${PAGE_NAME_ID}.filters.showInactive` })}
          value={filters.showInactivePonds}
          onChange={() => {
            let newFilters = { ...filters, showInactivePonds: !filters.showInactivePonds };
            updateFilters(newFilters);
          }}
        />
      </Box>
    </FilterSidebar>
  );
};

export default ListPondsFilter;
